/**
 * 激活会员弹层: 卡券核销与本人核销/赠送记录, 手机和桌面共用.
 *
 * 卡券由 BOSS 按渠道生成, 在线购买使用独立订单入口.
 */

import Alpine from 'alpinejs'
import { ApiError, api } from '../lib/api'
import { formatDate } from '../lib/format'
import { t } from '../lib/i18n'
import type { MembershipRecord } from '../types/api'
import type { AuthStore } from '../stores/auth'
import { toast } from '../stores/toast'

const PAGE_SIZE = 10
type RedeemTab = 'redeem' | 'history'

function authRef(): AuthStore | undefined {
  return Alpine.store('auth') as AuthStore | undefined
}

export function redeemPage() {
  let requestVersion = 0
  let previousFocus: HTMLElement | null = null
  let bodyWasLocked = false
  return {
    code: '',
    tab: 'redeem' as RedeemTab,
    records: [] as MembershipRecord[],
    total: 0,
    offset: 0,
    loading: false,
    loaded: false,
    historyError: '',
    ownerId: 0,

    /** 每次打开恢复激活标签; 换账号时清除前一账号的记录. */
    onOpen(userId: number | undefined): void {
      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
      bodyWasLocked = document.body.classList.contains('overflow-hidden')
      document.body.classList.add('overflow-hidden')
      requestAnimationFrame(() => document.getElementById('redeem-tab')?.focus())
      this.tab = 'redeem'
      this.code = ''
      authRef()?.clearError()
      if (this.ownerId !== userId) {
        requestVersion += 1
        this.records = []
        this.total = 0
        this.offset = 0
        this.loaded = false
        this.loading = false
        this.historyError = ''
        this.ownerId = userId ?? 0
      }
      // 邀请奖励可能由另一个用户的注册触发, 打开时重新读取当前会员状态
      const previousUntil = authRef()?.user?.member_until
      void api.auth.me().then((user) => {
        const store = authRef()
        if (store?.user && store.user.id === userId && store.user.member_until === previousUntil) store.user = user
      }).catch(() => {})
    },

    /** 关闭后恢复页面滚动与入口焦点, 桌面菜单已收起时回到菜单按钮. */
    onClose(): void {
      if (!bodyWasLocked) document.body.classList.remove('overflow-hidden')
      const target = previousFocus?.getClientRects().length
        ? previousFocus
        : document.querySelector<HTMLElement>('[data-user-menu]')
      requestAnimationFrame(() => target?.focus())
      previousFocus = null
    },

    /** 登录树被卸载时释放本弹层的滚动锁. */
    destroy(): void {
      requestVersion += 1
      this.onClose()
    },

    /** 原生 Tab 键只在当前弹层的可见控件之间循环. */
    trapFocus(event: KeyboardEvent): void {
      if (!(event.currentTarget instanceof HTMLElement)) return
      const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button, input, [tabindex="0"]')]
        .filter((element) => element.tabIndex >= 0 && !element.matches(':disabled') && element.getClientRects().length > 0)
      const first = controls[0]
      const last = controls[controls.length - 1]
      if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault()
        const target = event.shiftKey ? last : first
        target?.focus()
      }
    },

    /** 标签切换用同一个记录接口, 每次进入历史都刷新. */
    selectTab(tab: RedeemTab): void {
      this.tab = tab
      requestAnimationFrame(() => document.getElementById(tab === 'redeem' ? 'redeem-tab' : 'history-tab')?.focus())
      if (tab === 'history') void this.load(0)
    },

    /** 核销成功后留在当前弹层, 直接展示更新后的记录. */
    async submit(): Promise<void> {
      const ok = await authRef()?.redeem(this.code)
      if (!ok) return
      this.code = ''
      toast(t('toast.redeemSuccess'))
      this.selectTab('history')
    },

    /** 保留已展示的记录, 仅首次请求使用骨架占位. */
    async load(offset?: number): Promise<void> {
      const version = ++requestVersion
      const userId = this.ownerId
      this.loading = true
      this.historyError = ''
      try {
        const page = await api.membership.history({ limit: PAGE_SIZE, offset: offset ?? this.offset })
        if (version !== requestVersion || authRef()?.user?.id !== userId) return
        this.records = page.items
        this.total = page.meta.total
        this.offset = page.meta.offset
        this.loaded = true
      } catch (error) {
        if (version !== requestVersion || authRef()?.user?.id !== userId) return
        this.historyError = error instanceof ApiError ? error.message : t('toast.historyLoadFailed')
      } finally {
        if (version === requestVersion) this.loading = false
      }
    },

    get pageNumber(): number {
      return Math.floor(this.offset / PAGE_SIZE) + 1
    },

    get pageCount(): number {
      return Math.max(1, Math.ceil(this.total / PAGE_SIZE))
    },

    previousPage(): void {
      if (!this.loading && this.offset > 0) void this.load(this.offset - PAGE_SIZE)
    },

    nextPage(): void {
      if (!this.loading && this.offset + PAGE_SIZE < this.total) void this.load(this.offset + PAGE_SIZE)
    },

    /** 时长与既有规格同口径; 组合套餐回退为实际天数. */
    durationLabel(days: number): string {
      const key = ({ 1: 'duration.day', 7: 'duration.week', 30: 'duration.month', 365: 'duration.year' } as const)[
        days as 1 | 7 | 30 | 365
      ]
      return key ? t(key) : t('duration.days', { n: days })
    },

    dateLabel(iso: string): string {
      return formatDate(iso)
    },

    get canSubmit(): boolean {
      return this.code.trim().length >= 4
    },

    /** 输入时统一大写并去掉空格, 避免用户从聊天窗口复制带空格 */
    normalize(): void {
      this.code = this.code.replace(/\s+/g, '').toUpperCase()
    },
  }
}
